/**
 * ════════════════════════════════════════════════════════════════
 * native.js (r7.6 step 9) — what the web bundle does differently inside a tenant's Android shell.
 *
 *   initNative()            called once from main.jsx; no-op on the web
 *     • OTA: @capgo/capacitor-updater self-hosted — notifyAppReady() (else the updater rolls back to the
 *       previous bundle on next launch), then check GET <VITE_OTA_MANIFEST_URL>?version=<web>&native=<shell>
 *       → download + sha256 verify → set on next background (never mid-checkout); "Native update required"
 *       → route the user to Play.
 *     • deep links: @capacitor/app appUrlOpen (App Links https://<host>/e/<slug> …) → router navigate
 *     • back button: history back, exit on the front door
 *     • push: registration token → POST /me/push-tokens with tenant_id (step 6 contract), topic
 *       `tenant-<slug>` subscribed only while a tenant push consent exists (consent-safe by construction —
 *       see notify /notify/push); notification taps → navigate to data.url
 *     • status bar / splash from the tenant palette (TenantProvider updates when the palette loads)
 *
 *   Everything is best-effort and never throws into the app.
 * ════════════════════════════════════════════════════════════════
 */
import { Capacitor } from '@capacitor/core';
import { api } from '@/lib/api';
import { tokenStore } from '@/api/client';

export const isNative = () => typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform();
// read lazily: the values are build-time constants in a real bundle, but tests stub them after import
const env = () => (import.meta.env || {});
const tenantSlugOf = () => (env().VITE_TENANT_ID || '').toLowerCase().trim() || null;
const webVersion = () => env().VITE_WEB_VERSION || (typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0');
const nativeVersion = () => env().VITE_NATIVE_VERSION || '1.0.0';
const manifestUrl = () => env().VITE_OTA_MANIFEST_URL || (tenantSlugOf() ? `/api/app-updates/${tenantSlugOf()}/manifest` : null);
const PLAY_URL_KEY = 'snt:native:play_url';

let navigateFn = null;
export function bindNavigator(fn) { navigateFn = fn; }
function go(path) { if (navigateFn) navigateFn(path); else window.location.assign(path); }

// ─── OTA ───────────────────────────────────────────────────────────────────
let pendingBundle = null;
export function otaState() { return { pending: pendingBundle, web: webVersion(), native: nativeVersion() }; }

async function loadUpdater() {
  const mod = await import('@capgo/capacitor-updater').catch(() => null);
  return mod?.CapacitorUpdater || null;
}

export async function checkForUpdate({ apply = 'background' } = {}) {
  const MANIFEST_URL = manifestUrl(); const TENANT = tenantSlugOf();
  if (!isNative() || !MANIFEST_URL) return { skipped: true, reason: 'NO_MANIFEST' };
  const Updater = await loadUpdater();
  if (!Updater) return { skipped: true, reason: 'UPDATER_MISSING' };
  try {
    const url = `${MANIFEST_URL}${MANIFEST_URL.includes('?') ? '&' : '?'}version=${encodeURIComponent(webVersion())}&native=${encodeURIComponent(nativeVersion())}`;
    const r = await fetch(url, { headers: { Accept: 'application/json', ...(TENANT ? { 'X-Tenant-Id': TENANT } : {}) }, cache: 'no-store' });
    if (!r.ok) return { skipped: true, reason: `HTTP_${r.status}` };
    const m = await r.json();
    if (!m.version) {
      if (/native update required/i.test(m.message || '')) {
        try { sessionStorage.setItem('snt:native:update_required', m.min_native_version || '1'); } catch { /* ignore */ }
        window.dispatchEvent(new CustomEvent('snt:native-update-required', { detail: m }));
      }
      return { upToDate: true, message: m.message };
    }
    // download (the updater verifies `checksum` = sha256 of the zip) and stage
    const bundle = await Updater.download({ url: m.url, version: m.version, checksum: m.checksum });
    pendingBundle = { version: m.version, id: bundle.id };
    if (apply === 'now') { await Updater.set({ id: bundle.id }); return { applied: m.version }; }
    // `next`: swap on the next background → cold start; never mid-session (a checkout may be open)
    await Updater.next({ id: bundle.id });
    window.dispatchEvent(new CustomEvent('snt:ota-staged', { detail: { version: m.version } }));
    return { staged: m.version };
  } catch (err) {
    console.warn('[ota] check failed', err?.message || err);
    return { error: err?.message || String(err) };
  }
}

async function initOta() {
  const Updater = await loadUpdater();
  if (!Updater) return;
  // the running bundle is healthy — without this the updater reverts to the previous one after the timeout
  try { await Updater.notifyAppReady(); } catch { /* ignore */ }
  setTimeout(() => checkForUpdate(), 4000);
  // re-check when the app comes back after ≥ 10 min in the background
  try {
    const { App } = await import('@capacitor/app');
    let hiddenAt = 0;
    App.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) { hiddenAt = Date.now(); return; }
      if (hiddenAt && Date.now() - hiddenAt > 10 * 60e3) checkForUpdate();
    });
  } catch { /* ignore */ }
}

// ─── deep links + back button ──────────────────────────────────────────────
async function initAppLinks() {
  try {
    const { App } = await import('@capacitor/app');
    App.addListener('appUrlOpen', ({ url }) => {
      try {
        const u = new URL(url);
        go(u.pathname + u.search + u.hash);
      } catch { /* not a URL we understand */ }
    });
    App.addListener('backButton', ({ canGoBack }) => {
      const onRoot = ['/', '/login'].includes(window.location.pathname);
      if (onRoot || !canGoBack) App.exitApp(); else window.history.back();
    });
    const launch = await App.getLaunchUrl();
    if (launch?.url) { const u = new URL(launch.url); if (u.pathname !== '/') setTimeout(() => go(u.pathname + u.search), 0); }
  } catch { /* @capacitor/app not present */ }
}

// ─── push ──────────────────────────────────────────────────────────────────
let pushInitialised = false;
export async function initPush({ tenantSlug = tenantSlugOf(), pushConsent = false } = {}) {
  if (!isNative() || pushInitialised) return;
  pushInitialised = true;
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    PushNotifications.addListener('registration', async ({ value: token }) => {
      try { sessionStorage.setItem('snt:push:token', token); } catch { /* ignore */ }
      if (!tokenStore.access) return;   // registered again after login (see registerPushIfPossible)
      const { Device } = await import('@capacitor/device').catch(() => ({ Device: null }));
      const info = Device ? await Device.getInfo().catch(() => ({})) : {};
      const id = Device ? await Device.getId().catch(() => ({})) : {};
      await api.post('/api/me/push-tokens', { token, platform: 'android', device_id: id.identifier || null, device_model: info.model || null, app_version: `${nativeVersion()}+${webVersion()}`, tenant_id: tenantSlug || 'snt' }).catch(() => {});
      // the topic follows the tenant push consent on record (a fresh install re-derives it from the server)
      let consent = pushConsent;
      if (tenantSlug && tenantSlug !== 'snt') {
        const me = await api.get('/api/auth/consents/me').catch(() => null);
        consent = !!(me?.summary?.tenant_marketing?.[tenantSlug] || []).includes('push');
      }
      await syncTopic({ tenantSlug, pushConsent: consent });
    });
    PushNotifications.addListener('pushNotificationActionPerformed', ({ notification }) => {
      const url = notification?.data?.url || (notification?.data?.event_id ? `/e/${notification.data.event_id}` : null);
      if (url) go(url);
    });
    const perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'granted') await PushNotifications.register();
    // otherwise the preferences page asks (MyPreferencesPage → registerForPush) — no permission prompt on first launch
  } catch { /* plugin missing */ }
}

/** FCM topic `tenant-<slug>` follows the tenant push consent: subscribe when granted, unsubscribe when withdrawn. */
export async function syncTopic({ tenantSlug = tenantSlugOf(), pushConsent = false } = {}) {
  if (!isNative() || !tenantSlug || tenantSlug === 'snt') return;
  try {
    const mod = await import('@capacitor-community/fcm').catch(() => null);
    const FCM = mod?.FCM;
    if (!FCM) return;
    if (pushConsent) await FCM.subscribeTo({ topic: `tenant-${tenantSlug}` }); else await FCM.unsubscribeFrom({ topic: `tenant-${tenantSlug}` });
  } catch { /* ignore */ }
}

/** After login: (re)register so the token lands on this account. */
export async function registerPushIfPossible() {
  if (!isNative()) return;
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'granted') await PushNotifications.register();
  } catch { /* ignore */ }
}

// ─── chrome (status bar / splash) ──────────────────────────────────────────
export async function applyNativeChrome({ background = '#0A0A0B', mode = 'dark' } = {}) {
  if (!isNative()) return;
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.setBackgroundColor({ color: background });
    await StatusBar.setStyle({ style: mode === 'light' ? Style.Light : Style.Dark });
  } catch { /* ignore */ }
  try { const { SplashScreen } = await import('@capacitor/splash-screen'); await SplashScreen.hide(); } catch { /* ignore */ }
}

/** Where to send the user for a native update ("Native update required"). */
export function playStoreUrl(tenantPlayUrl = null) {
  try { return tenantPlayUrl || localStorage.getItem(PLAY_URL_KEY) || `https://play.google.com/store/apps/details?id=${env().VITE_APP_ID || 'com.showznticketz.app'}`; } catch { return 'https://play.google.com/store/apps'; }
}
export function rememberPlayUrl(url) { try { if (url) localStorage.setItem(PLAY_URL_KEY, url); } catch { /* ignore */ } }

export async function initNative() {
  if (!isNative()) return;
  await Promise.all([initOta(), initAppLinks(), initPush()]);
}
